import React, { useState, useEffect, useRef } from "react";
import {
  Badge,
  Card,
  Container,
  Center,
  Loader,
  Group,
  Pagination,
  ScrollArea,
  Stack,
  Kbd,
  Text,
  Switch,
  Menu,
  ActionIcon,
  rem,
  Grid,
} from "@mantine/core";
import { fetchGallery, deleteGalleryImage, createTrainingSet } from "../api";
import { GalleryImageCard } from "./GalleryImageCard";
import ImageUploader from "../Editor/ImageUploader";
import { useStore } from "../store";
import { useLocation } from "wouter";
import {
  IconDots,
  IconUserPlus,
  IconDeselect,
  IconLayersIntersect,
} from "@tabler/icons-react";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { ImageJobGenerateCard } from "./ImageJobGenerateCard";
import { GalleryVideoCard } from "./GalleryVideoCard";

function SuperGallery() {
  const galleryImages = useStore((state) => state.galleryImages);
  const setGalleryImages = useStore((state) => state.setGalleryImages);
  const jobs = useStore((state) => state.jobs);

  // const [galleryImages, setGalleryImages] = useState([]);
  const [activePage, setPage] = useState(1);
  const [userFilter, setUserFilter] = useState("");
  const [tagsFilter, setTagsFilter] = useState([]);
  const setImageData = useStore((state) => state.setImageData);
  const [, setLocation] = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const viewportRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 50em)");

  const selectedImages = useStore((state) => state.selectedImages);
  const setSelectedImages = useStore((state) => state.setSelectedImages);

  const showSelectedOnly = useStore((state) => state.showSelectedOnly);
  const setShowSelectedOnly = useStore((state) => state.setShowSelectedOnly);

  const pageSize = 26;

  function GalleryMenu() {
    return (
      <Menu shadow="md" width={280}>
        <Menu.Target>
          <ActionIcon variant="default" aria-label="Settings">
            <IconDots style={{ width: "70%", height: "70%" }} stroke={1.5} />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          {/* <Menu.Label>Selection</Menu.Label> */}
          <Menu.Item
            onClick={() => {
              setSelectedImages([]);
              setShowSelectedOnly(false);
            }}
            disabled={selectedImages.length === 0}
            leftSection={
              <IconDeselect style={{ width: rem(14), height: rem(14) }} />
            }
          >
            Deselect all
          </Menu.Item>
          <Menu.Item
            leftSection={
              <IconLayersIntersect
                style={{ width: rem(14), height: rem(14) }}
              />
            }
            disabled={selectedImages.length === 0}
          >
            <Switch
              label="Show selected only"
              checked={showSelectedOnly}
              disabled={selectedImages.length === 0}
              onChange={(event) =>
                setShowSelectedOnly(event.currentTarget.checked)
              }
            />
          </Menu.Item>
          <Menu.Item
            disabled={selectedImages.length === 0}
            onClick={() => {
              createTrainingSet(selectedImages)
                .then((data) => {
                  notifications.show({
                    title: "Success",
                    message: "Training set created",
                    color: "green",
                  });
                  setLocation(`/training-datasets/${data.id}`);
                })
                .catch((error) => {
                  notifications.show({
                    title: "An error occurred",
                    message: "" + error,
                    color: "red",
                  });
                });
            }}
            leftSection={
              <IconUserPlus style={{ width: rem(14), height: rem(14) }} />
            }
          >
            Create model
          </Menu.Item>
          {/* <Divider />
          <Menu.Item
            leftSection={
              <IconTrash style={{ width: rem(14), height: rem(14) }} />
            }
            onClick={() => {

            }}
            disabled={selectedImages.length === 0}
          >
            Delete selected
          </Menu.Item> */}
        </Menu.Dropdown>
      </Menu>
    );
  }

  useEffect(() => {
    setIsLoading(true);
    fetchGallery(activePage, pageSize, userFilter, tagsFilter).then((data) => {
      setGalleryImages(data);
      setIsLoading(false);
    });
  }, [activePage, userFilter, tagsFilter]);

  let onDelete = (logicalImageId) => {
    console.debug("onDelete called for image", logicalImageId);
    deleteGalleryImage(logicalImageId).then(() => {
      setGalleryImages({
        ...galleryImages,
        data: galleryImages.data.filter(
          (p) => p.logicalImageId != logicalImageId
        ),
      });
      notifications.show({
        title: "Image deleted",
        message: "The image has been deleted successfully.",
        color: "green",
      });
    });
  };

  let images = galleryImages.data;
  if (showSelectedOnly)
    images = galleryImages.data.filter((image) =>
      selectedImages.includes(image.logicalImageId)
    );

  const spans = { base: 12, xs: 6, sm: 4, md: 3, lg: 2, xl: 1 };

  return (
    <Container fluid p={0}>
      <Stack
        gap={0}
        style={{
          minHeight:
            "calc(100dvh - var(--app-shell-header-height, 0px) - 2 * var(--app-shell-padding, 0px) - var(--app-shell-footer-height, 0px))",
        }}
      >
        {isLoading ? (
          <Center mt="xl">
            <Loader mt="xl" size="xl" />
          </Center>
        ) : (
          <ScrollArea.Autosize
            style={{
              // must subtract another 28px for the pagination and 8px for Stack-gap
              height:
                "calc(100dvh - var(--app-shell-header-height, 0px) - 2 * var(--app-shell-padding, 0px) - var(--app-shell-footer-height, 0px) - 36px)",
            }}
            viewportRef={viewportRef}
          >
            {/* <SimpleGrid
              type="container"
              cols={{
                base: 1,
                "600px": 2,
                "900px": 3,
                "1200px": 4,
                "1500px": 5,
                "1800px": 6,
              }}
              spacing={{ base: "xs", "600px": "md" }}
            > */}
            <Grid
              p={5}
              gutter={10}
              type="container"
              // gutter={{ base: 5, xs: 'md', md: 'xl', xl: 50 }}
              breakpoints={{
                xs: "580px",
                sm: "860px",
                md: "1200px",
                lg: "1500px",
                xl: "1800px",
              }}
              // style={{
              //   gridTemplateColumns: "repeat(auto-fill, minmax(250px,1fr))",
              // }}
            >
              <Grid.Col
                span={spans}
              >
                <Card
                  withBorder
                  shadow="sm"
                  radius="md"
                  style={{ cursor: "pointer" }}
                >
                  <ImageUploader
                    // inline
                    uploadCompleted={(data) => {
                      setGalleryImages((imgs) => {
                        console.debug("Adding image to gallery", imgs, data);
                        return {
                          ...imgs,
                          data: [...data, ...imgs.data],
                        };
                      });
                    }}
                  />
                  <Text mt="xl" ta="center">
                    Pro tip: On keyboard devices, you can also paste images
                    using <Kbd>⌘</Kbd> + <Kbd>V</Kbd> or <Kbd>Ctrl</Kbd> +{" "}
                    <Kbd>V</Kbd>
                  </Text>
                </Card>
              </Grid.Col>

              {jobs?.map((job) => (
                <Grid.Col key={job.clientId} span={spans}>
                  <ImageJobGenerateCard job={job} />
                </Grid.Col>
              ))}

              {images?.map((image) =>
                image.videoUrl ? (
                  <Grid.Col key={image.logicalImageId} span={spans}>
                    <GalleryVideoCard
                      key={image.logicalImageId}
                      selectionMode={selectedImages.length > 0}
                      onDelete={onDelete}
                      checked={selectedImages.includes(image.logicalImageId)}
                      onCheckedChange={(checked) => {
                        if (checked)
                          setSelectedImages([
                            ...selectedImages,
                            image.logicalImageId,
                          ]);
                        else
                          setSelectedImages(
                            selectedImages.filter(
                              (id) => id !== image.logicalImageId
                            )
                          );
                      }}
                      onClick={() => {
                        setImageData(image);
                        setLocation(image.videoUrl ? "/video" : "/editor");
                      }}
                      image={image}
                    />
                  </Grid.Col>
                ) : (
                  <Grid.Col key={image.logicalImageId} span={spans}>
                    <GalleryImageCard
                      selectionMode={selectedImages.length > 0}
                      onDelete={onDelete}
                      checked={selectedImages.includes(image.logicalImageId)}
                      onCheckedChange={(checked) => {
                        if (checked)
                          setSelectedImages([
                            ...selectedImages,
                            image.logicalImageId,
                          ]);
                        else
                          setSelectedImages(
                            selectedImages.filter(
                              (id) => id !== image.logicalImageId
                            )
                          );
                      }}
                      onClick={() => {
                        setImageData(image);
                        // setImageData({
                        //   thumbnailUri: image.uri,
                        //   uri: image.fullsizeUri,
                        //   id: image.logicalImageId,
                        // });
                        setLocation(image.videoUrl ? "/video" : "/editor");
                      }}
                      image={image}
                    />
                  </Grid.Col>
                )
              )}
            </Grid>
          </ScrollArea.Autosize>
        )}
        <div style={{ flex: 1 }}></div>
        <Group p={0} m={0} gap={0} justify="space-between">
          <Pagination
            siblings={isMobile ? 1 : 2}
            boundaries={isMobile ? 0 : 1}
            withControls={!isMobile}
            size="sm"
            m={0}
            value={activePage}
            onChange={setPage}
            total={Math.ceil(galleryImages.totalCount / pageSize)}
          />
          {selectedImages.length > 0 && (
            <Badge radius="xs" size="lg">
              {selectedImages.length} selected
            </Badge>
          )}
          {/* <Switch
            label="Show selected only"
            visibleFrom="md"
            disabled={selectedImages.length === 0}
            checked={showSelectedOnly}
            onChange={(event) =>
              setShowSelectedOnly(event.currentTarget.checked)
            }
          /> */}
          <GalleryMenu />
        </Group>
      </Stack>
    </Container>
  );
}

export default SuperGallery;
